/**
 * hooks.ts
 *
 * React hooks that wire the football tracker to a <canvas> element.
 *
 * Size model
 * ──────────
 * Canvas pixel size is driven by a *wrapper div*, not the canvas itself.
 * This solves two problems:
 *
 *  1. The wrapper can start at 0×0 (hidden panel, collapsed tab, display:none
 *     ancestor) — the ResizeObserver skips zero-size observations and draws
 *     the first frame only when the container becomes actually visible.
 *
 *  2. On every resize the canvas attributes are updated and the current frame
 *     is redrawn immediately at the new size — no blurriness from CSS scaling.
 *
 * Typical JSX:
 *
 *   const wrapperRef = useRef<HTMLDivElement>(null);
 *   const canvasRef  = useRef<HTMLCanvasElement>(null);
 *
 *   useFootballPlayback(wrapperRef, canvasRef, { ... });
 *
 *   <div ref={wrapperRef} style={{ width: "100%", height: 400 }}>
 *     <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
 *   </div>
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { buildGroundContext, normalizeGroundVertices } from "./ground";
import {
  createPlayback,
  processAllPlayers,
  renderFrame,
  DEFAULT_RENDER_OPTIONS,
} from "./tracking";

import type {
  CoordType,
  GpsPoint,
  GroundContext,
  NormalizedGround,
  PlayerInput,
  PlaybackController,
  ProcessedPlayer,
  RenderOptions,
  VertexInput,
} from "./types";

// ─── useWrapperSize ───────────────────────────────────────────────────────────

export interface Size {
  width: number;
  height: number;
}

/**
 * Observes a wrapper div and keeps the canvas pixel attributes in sync.
 *
 * - Skips 0×0 observations (hidden / collapsed containers).
 * - devicePixelRatio-aware: the canvas bitmap is scaled for crisp HiDPI
 *   rendering while the CSS size is unchanged.
 * - Returns CSS size (not bitmap size) for layout calculations.
 * - Calls onResize whenever positive dimensions are observed.
 * - Reads the current size immediately on mount in case the wrapper already
 *   has positive dimensions (visible on first render).
 */
export function useWrapperSize(
  wrapperRef: React.RefObject<HTMLElement | null>,
  canvasRef:  React.RefObject<HTMLCanvasElement | null>,
  onResize?:  (size: Size) => void,
): Size {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const apply = (w: number, h: number) => {
      if (w <= 0 || h <= 0) return;   // skip until actually visible

      const canvas = canvasRef.current;
      if (canvas) {
        const dpr = window.devicePixelRatio ?? 1;
        // Only update if size actually changed to avoid clearing the canvas
        // unnecessarily on every animation frame
        const newW = Math.round(w * dpr);
        const newH = Math.round(h * dpr);
        if (canvas.width !== newW || canvas.height !== newH) {
          canvas.width  = newW;
          canvas.height = newH;
          // Re-apply DPR scale — setting width/height resets the context
          canvas.getContext("2d")?.scale(dpr, dpr);
        }
      }

      const next: Size = { width: Math.round(w), height: Math.round(h) };
      setSize((prev) =>
        prev.width === next.width && prev.height === next.height ? prev : next,
      );
      onResizeRef.current?.(next);
    };

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.borderBoxSize?.length) {
          const { inlineSize: w, blockSize: h } = entry.borderBoxSize[0];
          apply(w, h);
        } else {
          apply(entry.contentRect.width, entry.contentRect.height);
        }
      }
    });

    ro.observe(wrapper);

    // Immediate read in case the wrapper is already visible
    const rect = wrapper.getBoundingClientRect();
    apply(rect.width, rect.height);

    return () => ro.disconnect();
  // refs are stable — no deps needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return size;
}

// ─── useFootballTracker ───────────────────────────────────────────────────────

export interface UseFootballTrackerOptions {
  groundVertices:      VertexInput[];
  coordType?:          CoordType | "auto";
  gpsAnchor?:          GpsPoint;
  players:             PlayerInput[];
  renderOptions?:      Partial<RenderOptions>;
  /** Correct non-rectangular ground via projective homography (default: false) */
  correctPerspective?: boolean;
}

export interface UseFootballTrackerResult {
  ground:      NormalizedGround | null;
  groundCtx:   GroundContext | null;
  players:     ProcessedPlayer[];
  currentTime: number;
  tMin:        number;
  tMax:        number;
  seek:        (t: number) => void;
  /** CSS size of the wrapper div — {0,0} until the container is visible */
  canvasSize:  Size;
}

/**
 * Core hook: normalises ground + processes players + wires wrapper → canvas.
 * No animation — use useFootballPlayback for that.
 */
export function useFootballTracker(
  wrapperRef: React.RefObject<HTMLElement | null>,
  canvasRef:  React.RefObject<HTMLCanvasElement | null>,
  options:    UseFootballTrackerOptions,
): UseFootballTrackerResult {
  const {
    groundVertices,
    coordType = "auto",
    gpsAnchor,
    players: rawPlayers,
    renderOptions,
    correctPerspective = false,
  } = options;

  const ground = useMemo(
    () => normalizeGroundVertices(groundVertices, coordType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(groundVertices), coordType],
  );

  const groundCtx = useMemo(
    () => buildGroundContext(ground, { gpsAnchor, correctPerspective }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ground, JSON.stringify(gpsAnchor), correctPerspective],
  );

  const players = useMemo(
    () =>
      processAllPlayers(
        rawPlayers,
        groundCtx,
        coordType === "auto" ? "local" : coordType,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rawPlayers), groundCtx, coordType],
  );

  const allT = useMemo(() => players.flatMap((p) => p.samples.map((s) => s.t)), [players]);
  const tMin = useMemo(() => (allT.length ? Math.min(...allT) : 0), [allT]);
  const tMax = useMemo(() => (allT.length ? Math.max(...allT) : 0), [allT]);

  const [currentTime, setCurrentTime] = useState(tMin);

  const mergedOpts = useMemo(
    () => ({ ...DEFAULT_RENDER_OPTIONS, ...renderOptions }),
    [renderOptions],
  );

  // Keep a ref to current time so the resize callback can use it without
  // being a dependency of useWrapperSize
  const currentTimeRef = useRef(tMin);
  currentTimeRef.current = currentTime;

  const redraw = useCallback(
    (t: number) => {
      const canvas = canvasRef.current;
      if (canvas && canvas.width > 0 && canvas.height > 0 && players.length) {
        renderFrame(canvas, groundCtx, players, t, mergedOpts);
      }
    },
    [canvasRef, groundCtx, players, mergedOpts],
  );

  // When the wrapper is resized, redraw at the current timestamp.
  // setTimeout(0) ensures the canvas attributes are written before we draw.
  const canvasSize = useWrapperSize(wrapperRef, canvasRef, () => {
    setTimeout(() => redraw(currentTimeRef.current), 0);
  });

  const seek = useCallback(
    (t: number) => {
      setCurrentTime(t);
      redraw(t);
    },
    [redraw],
  );

  // Re-render when data / context changes
  useEffect(() => {
    if (players.length) {
      setCurrentTime(tMin);
      redraw(tMin);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groundCtx, players, tMin]);

  return { ground, groundCtx, players, currentTime, tMin, tMax, seek, canvasSize };
}

// ─── useFootballPlayback ──────────────────────────────────────────────────────

export interface UseFootballPlaybackOptions extends UseFootballTrackerOptions {
  speed?:    number;
  loop?:     boolean;
  autoPlay?: boolean;
}

export interface UseFootballPlaybackResult extends UseFootballTrackerResult {
  isPlaying: boolean;
  play:      () => void;
  pause:     () => void;
  toggle:    () => void;
  speed:     number;
  setSpeed:  (s: number) => void;
}

/**
 * Full playback hook. The canvas is sized from the wrapper div.
 * Resizing while playing is handled automatically — the next RAF frame
 * uses the updated canvas dimensions with no restart needed.
 */
export function useFootballPlayback(
  wrapperRef: React.RefObject<HTMLElement | null>,
  canvasRef:  React.RefObject<HTMLCanvasElement | null>,
  options:    UseFootballPlaybackOptions,
): UseFootballPlaybackResult {
  const { autoPlay = false, loop = true } = options;

  const tracker = useFootballTracker(wrapperRef, canvasRef, options);
  const { groundCtx, players } = tracker;

  const [isPlaying,  setIsPlaying]  = useState(false);
  const [speed,      setSpeedState] = useState(options.speed ?? 1);

  const controllerRef  = useRef<PlaybackController | null>(null);

  // Stable refs — avoid stale closures inside play/setSpeed
  const groundCtxRef  = useRef(groundCtx);
  const playersRef    = useRef(players);
  const speedRef      = useRef(speed);
  const renderOptsRef = useRef(options.renderOptions);
  groundCtxRef.current  = groundCtx;
  playersRef.current    = players;
  speedRef.current      = speed;
  renderOptsRef.current = options.renderOptions;

  // Tear down when data changes
  useEffect(() => {
    controllerRef.current?.stop();
    controllerRef.current = null;
    setIsPlaying(false);
  }, [groundCtx, players]);

  const play = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !playersRef.current.length) return;
    if (canvas.width <= 0 || canvas.height <= 0) return;   // not yet visible

    if (!controllerRef.current) {
      controllerRef.current = createPlayback(
        canvas,
        groundCtxRef.current,
        playersRef.current,
        { speed: speedRef.current, loop, renderOptions: renderOptsRef.current },
      );
    } else {
      controllerRef.current.start();
    }
    setIsPlaying(true);
  }, [canvasRef, loop]);

  const pause = useCallback(() => {
    controllerRef.current?.stop();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(
    () => { if (isPlaying) pause(); else play(); },
    [isPlaying, play, pause],
  );

  const seek = useCallback(
    (t: number) => {
      tracker.seek(t);
      controllerRef.current?.seek(t);
    },
    [tracker],
  );

  const setSpeed = useCallback(
    (s: number) => {
      setSpeedState(s);
      speedRef.current = s;
      if (controllerRef.current) {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const wasPlaying = isPlaying;
        controllerRef.current.stop();
        controllerRef.current = createPlayback(
          canvas,
          groundCtxRef.current,
          playersRef.current,
          { speed: s, loop, renderOptions: renderOptsRef.current },
        );
        if (!wasPlaying) controllerRef.current.stop();
      }
    },
    [canvasRef, isPlaying, loop],
  );

  // autoPlay: poll until canvas has positive size (wrapper may start hidden)
  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => {
      const canvas = canvasRef.current;
      if (canvas && canvas.width > 0 && canvas.height > 0) {
        clearInterval(id);
        play();
      }
    }, 50);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => { controllerRef.current?.stop(); }, []);

  return { ...tracker, seek, isPlaying, play, pause, toggle, speed, setSpeed };
}

// ─── useGroundContext (lightweight) ──────────────────────────────────────────

export function useGroundContext(
  vertices:           VertexInput[],
  coordType:          CoordType | "auto" = "auto",
  gpsAnchor?:         GpsPoint,
  correctPerspective = false,
): GroundContext {
  const ground = useMemo(
    () => normalizeGroundVertices(vertices, coordType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(vertices), coordType],
  );
  return useMemo(
    () => buildGroundContext(ground, { gpsAnchor, correctPerspective }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ground, JSON.stringify(gpsAnchor), correctPerspective],
  );
}
